import { useCallback, useRef } from "react";

import * as THREE from "three";
import * as FRAGS from "@thatopen/fragments";

import { Area, Line } from "@treegram/viewer-core";
import { DrawLineTool } from "@treegram/tools-draw-line";
import { DrawPolygonTool } from "@treegram/tools-draw-polygon";
import { CameraTool } from "@treegram/tools-camera";
import { HighlighterTool } from "@treegram/tools-highlighter";

import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import useStoreyMeasuresContext from "@/pages/takeoff/layouts/measures/layouts/storey-measures/context/measures/use-storey-measures-context.ts";
import useProjectMeasureContext from "@/pages/takeoff/context/project-measure/use-project-measure-context.ts";
import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";

import { ModelingMeasureRepresentation, ModelingType } from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import Measure from "@/pages/takeoff/context/project-measure/models/measure.ts";
import GeometryRepresentation from "@/pages/takeoff/context/project-measure/models/geometry-representation.ts";
import GeometryRepresentationOperation from "@/models/enums/geometry-representation-operation.ts";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";

export interface DrawingProps {
    measureTypeId?: string;
    drawingColor: THREE.Color;
    drawingAlpha: number;
    drawingMeasureRepresentation: ModelingMeasureRepresentation;
    drawingDataInformations?: Record<string, unknown>;
}

const useStoreyMeasuresDrawingTool = () => {
    
    const { getTool } = useViewerContext();
    const { getMeasureType } = useProjectMeasureContext();
    const { measures, addMeasure, handleGeometriesSelected } = useStoreyMeasuresContext();
    const { addItems } = useViewerItemsVisibilityContext();
    const { setIsInDrawingMode } = useModelingToolsContext();
    
    const tempIds = useRef<Map<string, string[]>>(new Map<string, string[]>());
    const currentDrawingTypeRef = useRef<ModelingType>(ModelingType.Point);
    const currentDrawingPropsRef = useRef<DrawingProps | undefined>(undefined);

    const setTempId = useCallback((type: ModelingType, measureRepresentation: ModelingMeasureRepresentation, id: string) => {
        const key = `${type}:${measureRepresentation}`;
        let ids = tempIds.current.get(key);
        if (ids == null) {
            ids = [];
            tempIds.current.set(key, ids);
        }

        ids.push(id);
    }, []);

    const createGeometry = useCallback((id: string) =>
        new GeometryRepresentation(id, GeometryRepresentationOperation.Addition, crypto.randomUUID()), []);

    const handleAddMeasure = useCallback((dataInformations: Record<string, unknown> | undefined) => {
        const measureTypeId = currentDrawingPropsRef.current?.measureTypeId;
        if (!measureTypeId) return;

        const measuresOfThisType = measures.filter(m => m.measureTypeId === measureTypeId);
        const quantity = measuresOfThisType.length;
        const name = getMeasureType(measureTypeId)?.name ?? "";
        const measure = new Measure(measureTypeId, `${name} - ${quantity + 1}`, crypto.randomUUID(), dataInformations);
        const drawingType = currentDrawingTypeRef.current;
        const drawingMeasureRepresentation = currentDrawingPropsRef.current?.drawingMeasureRepresentation ?? ModelingMeasureRepresentation.LineImprint;
        const key = `${drawingType}:${drawingMeasureRepresentation}`;
        const geometriesIds = tempIds.current.get(key);
        if (geometriesIds != null) {
            addItems(geometriesIds);
            const geometries = geometriesIds.map(createGeometry);
            measure.setGeometryRepresentations(geometries);
        }

        addMeasure(measure);
        tempIds.current.set(key, []);
    }, [measures, addMeasure, createGeometry, getMeasureType, addItems]);

    const lineDrawingEndedCallback = useCallback(async (e: Line) => {
        if (getTool(DrawLineTool) == null) return;

        const measureRep = currentDrawingPropsRef.current?.drawingMeasureRepresentation ?? ModelingMeasureRepresentation.LineImprint;
        setTempId(ModelingType.Line, measureRep, e.id);
        handleAddMeasure(e.dataInformations);
    }, [getTool, handleAddMeasure, setTempId]);

    const surfaceDrawingEndedCallback = useCallback(async (e: Area) => {
        if (getTool(DrawPolygonTool) == null) return;

        const measureRep = currentDrawingPropsRef.current?.drawingMeasureRepresentation ?? ModelingMeasureRepresentation.LineImprint;
        setTempId(ModelingType.Surface, measureRep, e.id);
        handleAddMeasure(e.dataInformations);
    }, [getTool, handleAddMeasure, setTempId]);
    
    const startDrawing = useCallback(async (drawingType: ModelingType, isSnappingEnabled: boolean, drawingProps: DrawingProps) => {
        currentDrawingTypeRef.current = drawingType;
        currentDrawingPropsRef.current = drawingProps;

        const lineTool = getTool(DrawLineTool);
        const polygonTool = getTool(DrawPolygonTool);
        const cameraTool = getTool(CameraTool);
        const highlighter = getTool(HighlighterTool);

        if (cameraTool != null) {
            cameraTool.setNavigationMode("Plan");
        }

        if (highlighter != null) {
            await highlighter.deactivate();
        }

        const snappings = isSnappingEnabled ? [FRAGS.SnappingClass.LINE, FRAGS.SnappingClass.POINT, FRAGS.SnappingClass.FACE] : [];

        switch (drawingType) {
            case ModelingType.Point:
                break;
            case ModelingType.Line: {
                if (lineTool == null) return;
                await lineTool.activate();

                lineTool.setColor(drawingProps.drawingColor);
                lineTool.setAlpha(drawingProps.drawingAlpha);
                lineTool.setMeasureRepresentation(drawingProps.drawingMeasureRepresentation);
                lineTool.setSnappings(snappings);
                if (drawingProps.drawingDataInformations)
                    lineTool.setDataInformations(drawingProps.drawingDataInformations);
                lineTool.draw();
                lineTool.onEndedCreation.add(lineDrawingEndedCallback);
                break;
            }
            case ModelingType.Polyline:
                break;
            case ModelingType.Surface: {
                if (polygonTool == null) return;
                await polygonTool.activate();

                polygonTool.setColor(drawingProps.drawingColor);
                polygonTool.setAlpha(drawingProps.drawingAlpha);
                polygonTool.setMeasureRepresentation(drawingProps.drawingMeasureRepresentation);
                polygonTool.setSnappings(snappings);
                if (drawingProps.drawingDataInformations)
                    polygonTool.setDataInformations(drawingProps.drawingDataInformations);
                polygonTool.draw();
                polygonTool.onEndedCreation.add(surfaceDrawingEndedCallback);
                break;
            }
            default:
                break;
        }

        setIsInDrawingMode(true);
    }, [getTool, lineDrawingEndedCallback, setIsInDrawingMode, surfaceDrawingEndedCallback]);
    
    const stopDrawing = useCallback(async (clear: boolean) => {
        const lineTool = getTool(DrawLineTool);
        const polygonTool = getTool(DrawPolygonTool);
        const cameraTool = getTool(CameraTool);
        const highlighter = getTool(HighlighterTool);

        if (cameraTool != null) {
            cameraTool.setNavigationMode("Orbit");
        }

        if (highlighter != null) {
            await highlighter.activate();
            highlighter.onItemsSelected.add(handleGeometriesSelected);
        }

        lineTool?.onEndedCreation.remove(lineDrawingEndedCallback);
        polygonTool?.onEndedCreation.remove(surfaceDrawingEndedCallback);

        if(clear) {
            lineTool?.clear();
            polygonTool?.clear();
        }
        
        await lineTool?.deactivate();
        await polygonTool?.deactivate();

        setIsInDrawingMode(false);
    }, [getTool, handleGeometriesSelected, lineDrawingEndedCallback, setIsInDrawingMode, surfaceDrawingEndedCallback]);

    return {
        startDrawing,
        stopDrawing
    };
};

export default useStoreyMeasuresDrawingTool;